import { useEffect, useMemo, useRef, useState } from 'react'

import { errorView, useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  CommonProductScreen,
  PLATFORM_WINDOWS,
  SourceProductExtension,
  buildCommonProductVm,
  createBodyOf,
  newUuid,
  patchBodyOf,
  peekScanFrame,
  platformCatalogProducts,
  platformCommonProduct,
  platformCommonProductArchive,
  platformCommonProductCreate,
  platformCommonProductPatch,
  scanEventRecorded,
  scanEventText,
  scanFrameFile,
  scanFrameUrl,
  type CommonProductActions,
  type CommonProductEdited,
  type DuplicateCheckVm,
  type DuplicateMatchVm,
  type ProductPrefill,
  type ProductSaveRequest,
  type ScanEventKind,
} from '@krp/platform-ui'
import { SYNC_WINDOWS } from '@krp/sync-ui'

import { useAppDispatch } from '../../app/hooks'
import type { AppDispatch } from '../../app/types'
import { CONNECTION_FORMS, SOURCE_PRODUCTS } from '../../composition/connectionForms'
import { CommonPhotoRegistryBinding } from './CommonPhotoRegistryBinding'
import { errorCodeOf } from './unknownShapes'
import { usePhotoUploadTask } from './usePhotoUploadTask'
import { useScanOrigin } from './useScanOrigin'

const kindTitle = (kind: string) => CONNECTION_FORMS.titleOf(kind)

/** Сколько строк каталога просим, чтобы найти среди них этот товар и его связи с источниками. */
const SOURCES_LOOKUP_SIZE = 50

/** Сколько похожих по названию показываем в «Проверке дубля»: больше — уже не «этот же товар». */
const DUPLICATES_BY_NAME = 3
const DUPLICATES_BY_BARCODE = 5

/**
 * «Проверка дубля» перед созданием из сканирования (RP-FB56): поиск общего каталога по штрихкодам и по названию (единая служба
 * текстового поиска, отдельных сверок строк здесь нет). Нашлось похожее — человек решает; не получилось проверить — тоже решает он.
 */
async function findDuplicates(dispatch: AppDispatch, form: { name: string; barcodes: readonly string[] }): Promise<DuplicateMatchVm[]> {
  const found = new Map<string, DuplicateMatchVm>()
  const ask = async (query: string, because: string, limit: number) => {
    const page = await dispatch(
      platformCatalogProducts.initiate({ query, page: 1, pageSize: limit }, { forceRefetch: true, subscribe: false }),
    ).unwrap()
    for (const item of page.items.slice(0, limit)) {
      if (!found.has(item.id)) found.set(item.id, { id: item.id, name: item.displayName, sku: item.sku, because })
    }
  }
  for (const code of form.barcodes) await ask(code, 'найден по штрихкоду', DUPLICATES_BY_BARCODE)
  if (form.name.trim() !== '') await ask(form.name.trim(), 'похожее название', DUPLICATES_BY_NAME)
  return [...found.values()]
}

/**
 * Binding окна «Общий товар» (RP-FB50): и просмотр/правка (`/catalog/products/:productId`), и создание
 * (`/catalog/product-new`) — одно окно. Ключ операции — намерение человека: на одно и то же тело запроса он один, повтор
 * после обрыва ответа несёт тот же ключ, и сервер вернёт исполненный итог, а не сделает второй раз.
 * Устаревшая версия (409) перечитывает товар: экран сам сверит «было / сейчас / ваше».
 *
 * Создание из сканирования (RP-FB56, `?from=scan`): название и штрихкод приходят из адреса, кадр — с устройства. «Создать» сперва
 * запускает «Проверку дубля»; нашлось похожее — «Это он — прикрепить фото», «Открыть» или явное «Всё равно создать». После записи
 * товара кадр уходит в фото (ход и ошибки на виду, «Повторить» с тем же ключом операции), затем окно закрывается и сканер
 * возвращается к видоискателю. Товар создан, а фото не ушло — товар не откатывается: «Повторить» или «Открыть товар без фото».
 */
export function CommonProductBinding() {
  const handle = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const origin = useScanOrigin()
  const photoTask = usePhotoUploadTask()
  const [duplicate, setDuplicate] = useState<DuplicateCheckVm | null>(null)
  /** Запрос создания, который ждёт решения по найденному дублю. */
  const pendingCreate = useRef<ProductSaveRequest | null>(null)
  /** Созданный из сканирования товар: нужен «Повторить» после сбоя фото и записи в ленту. */
  const createdScan = useRef<CommonProductEdited | null>(null)
  const failureLogged = useRef(false)
  const create = handle.kind === PLATFORM_WINDOWS.commonProductNew
  const scan = create && origin !== null
  const productId = handle.params.productId ?? ''
  const operations = useRef(new Map<string, string>())
  const skip = create || productId === ''

  const args = useMemo(() => ({ productId }), [productId])
  const result = platformCommonProduct.useQuery(args, { skip })
  const [createProduct, createResult] = platformCommonProductCreate.useMutation()
  const [patchProduct, patchResult] = platformCommonProductPatch.useMutation()
  const [archiveProduct, archiveResult] = platformCommonProductArchive.useMutation()

  const product = result.data
  // Связи с источниками лежат в строке общего каталога: ищем её по наименованию и узнаём по идентификатору.
  const lookupArgs = useMemo(() => ({ query: product?.name ?? '', page: 1, pageSize: SOURCES_LOOKUP_SIZE }), [product?.name])
  const lookup = platformCatalogProducts.useQuery(lookupArgs, { skip: skip || product === undefined })
  const catalogItem = lookup.data?.items.find((item) => item.id === productId)

  const saveResult = create ? createResult : patchResult
  const baseVm = buildCommonProductVm({
    mode: create ? 'create' : 'edit',
    result: create
      ? null
      : {
          ...(result.data !== undefined ? { data: result.data } : {}),
          ...(result.error !== undefined ? { error: result.error } : {}),
          isUninitialized: result.isUninitialized,
          isLoading: result.isFetching,
          isError: result.isError,
          isSuccess: result.isSuccess,
        },
    save: saveResult,
    archive: archiveResult,
    catalogItem,
    catalogLoaded: catalogItem !== undefined,
    kindTitle,
  })

  // Создание из сканирования: название, штрихкод и кадр приходят из адреса и с устройства.
  const scanFrameId = origin?.frameId ?? ''
  const prefill: ProductPrefill | undefined =
    scan && origin !== null
      ? { name: origin.name, barcode: origin.barcode, frameUrl: scanFrameUrl(scanFrameId), frameLost: peekScanFrame(scanFrameId) === undefined }
      : undefined
  const { run: runPhoto, retry: retryPhoto, accept: acceptPhoto, cancel: cancelPhoto, dismiss: dismissPhoto, view: photoRun } = photoTask
  const vm = scan ? { ...baseVm, prefill, duplicate, photoRun } : baseVm

  // Создали — окно становится окном этого товара: правка, фото и источники доступны сразу. Из сканирования окно ведёт себя иначе
  // (фото, затем закрытие и возврат в сканер) — это делают действия ниже, а не эта подмена.
  const created = createResult.data
  const { replaceWith, setCloseGuard } = handle
  useEffect(() => {
    if (!create || scan || created === undefined) return
    setCloseGuard?.(null)
    replaceWith(PLATFORM_WINDOWS.commonProduct, { productId: created.product.id })
  }, [create, scan, created, replaceWith, setCloseGuard])

  // Чужая правка успела раньше (409): перечитываем товар, экран сверит поля и покажет расхождения.
  const refetch = result.refetch
  const stale = patchResult.isError && errorCodeOf(patchResult.error) === 'product-version-conflict'
  useEffect(() => {
    if (stale && !skip) void refetch()
  }, [stale, patchResult.error, refetch, skip])

  const latest = useRef(handle)
  useEffect(() => {
    latest.current = handle
  }, [handle])

  const actions = useMemo<CommonProductActions>(() => {
    /** Ключ операции для тела запроса: тот же запрос — тот же ключ. */
    const operationOf = (probe: unknown): string => {
      const key = JSON.stringify(probe)
      const known = operations.current.get(key)
      if (known !== undefined) return known
      const fresh = newUuid()
      operations.current.set(key, fresh)
      return fresh
    }
    const recordEvent = (kind: ScanEventKind, name: string, id: string | null) =>
      dispatch(scanEventRecorded({ kind, text: scanEventText(kind, name), productId: id, at: Date.now() }))
    /** Товар создан и фото лежит временной загрузкой: смена получает «Создано», окно закрывается, сканер возвращается к видоискателю. */
    const finishCreated = (product: CommonProductEdited) => {
      recordEvent('created', product.name, product.id)
      latest.current.setCloseGuard?.(null)
      latest.current.close()
    }
    /** Товар создан, фото не будет: открываем товар как есть — фото добавят в его карточке. */
    const finishWithoutPhoto = (product: CommonProductEdited) => {
      dismissPhoto()
      recordEvent('created', product.name, product.id)
      latest.current.setCloseGuard?.(null)
      latest.current.replaceWith(PLATFORM_WINDOWS.commonProduct, { productId: product.id })
    }
    const afterCreate = async (product: CommonProductEdited) => {
      createdScan.current = product
      failureLogged.current = false
      const stored = peekScanFrame(scanFrameId)
      if (stored === undefined) {
        finishCreated(product)
        return
      }
      const outcome = await runPhoto(product.id, scanFrameFile(stored), scanFrameUrl(scanFrameId))
      if (outcome.ok) {
        finishCreated(product)
        return
      }
      // Человек отказался от фото («Отмена» на проверке обрезки): товар открывается без фото, это делает photoCancel — в ленту «Не сохранено» не пишем.
      if (outcome.cancelled) return
      // Сбой фото — не успех: в ленту «Не сохранено — повторить», счётчик «Создано» не растёт, пока фото не легло.
      if (!failureLogged.current) {
        failureLogged.current = true
        recordEvent('failed', product.name, product.id)
      }
    }
    const startCreate = (request: Extract<ProductSaveRequest, { mode: 'create' }>) => {
      const sent = createProduct({ body: createBodyOf(request.form, operationOf(createBodyOf(request.form, ''))) })
      if (!scan) {
        void sent
        return
      }
      void sent.unwrap().then(
        (result) => afterCreate(result.product),
        () => undefined,
      )
    }
    return {
      reload: () => {
        if (!skip) void refetch()
      },
      save: (request: ProductSaveRequest) => {
        if (request.mode === 'create') {
          if (!scan) {
            startCreate(request)
            return
          }
          // «Проверка дубля» до записи: нашлось похожее — решает человек; проверка не удалась — тоже он, а не молчаливое создание.
          pendingCreate.current = request
          setDuplicate({ kind: 'checking' })
          void findDuplicates(dispatch, request.form).then(
            (matches) => {
              if (matches.length === 0) {
                pendingCreate.current = null
                setDuplicate(null)
                startCreate(request)
              } else {
                setDuplicate({ kind: 'found', matches })
              }
            },
            (failure: unknown) => setDuplicate({ kind: 'failed', message: errorView(failure).message }),
          )
          return
        }
        const probe = patchBodyOf(request.base, request.form, '', request.expectedVersion)
        void patchProduct({ productId, body: patchBodyOf(request.base, request.form, operationOf(probe), request.expectedVersion) })
      },
      archive: (expectedVersion) => {
        const operationId = operationOf({ archive: productId, expectedVersion })
        void archiveProduct({ productId, operationId, expectedVersion })
      },
      openSync: () => openWindow(SYNC_WINDOWS.home),
      close: () => latest.current.close(),
      closeGuard: (guard) => latest.current.setCloseGuard?.(guard),
      duplicateAttach: (id) => {
        setDuplicate(null)
        latest.current.setCloseGuard?.(null)
        // Кадр уже в адресе окна (`frame`): окно подтверждения прочитает его оттуда, а возврат из него — в сканер.
        latest.current.replaceWith(PLATFORM_WINDOWS.photoAttach, { productId: id })
      },
      duplicateOpen: (id) => {
        setDuplicate(null)
        latest.current.setCloseGuard?.(null)
        latest.current.replaceWith(PLATFORM_WINDOWS.commonProduct, { productId: id })
      },
      duplicateCreateAnyway: () => {
        const request = pendingCreate.current
        pendingCreate.current = null
        setDuplicate(null)
        if (request !== null && request.mode === 'create') startCreate(request)
      },
      duplicateDismiss: () => {
        pendingCreate.current = null
        setDuplicate(null)
      },
      photoRetry: () => {
        const product = createdScan.current
        if (product === null) return
        void retryPhoto().then((outcome) => {
          if (outcome.ok) finishCreated(product)
        })
      },
      photoCancel: () => {
        cancelPhoto()
        const product = createdScan.current
        if (product !== null) finishWithoutPhoto(product)
      },
      photoAcceptCrop: (box) => void acceptPhoto(box),
      photoClose: () => {
        const product = createdScan.current
        if (product !== null) finishWithoutPhoto(product)
      },
      openWithoutPhoto: () => {
        const product = createdScan.current
        if (product !== null) finishWithoutPhoto(product)
      },
    }
  }, [
    acceptPhoto,
    archiveProduct,
    cancelPhoto,
    createProduct,
    dismissPhoto,
    dispatch,
    openWindow,
    patchProduct,
    productId,
    refetch,
    retryPhoto,
    runPhoto,
    scan,
    scanFrameId,
    skip,
  ])

  return (
    <CommonProductScreen
      vm={vm}
      actions={actions}
      renderSourceExtension={(source) => (
        <SourceProductExtension
          registry={SOURCE_PRODUCTS}
          kind={source.kind}
          resourceId={source.resourceId}
          remoteKey={source.remoteKey}
          onOpenCard={(card) => openWindow(card.kind, card.params)}
        />
      )}
      photos={
        create || productId === '' ? undefined : (
          <CommonPhotoRegistryBinding productId={productId} />
        )
      }
    />
  )
}
